import React, { useState } from 'react';
import { X, Send, Star, Bug, Lightbulb, MessageSquare, Image as ImageIcon, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { User } from '../types';
import { submitFeedback, FeedbackType } from '../services/feedbackService';

interface FeedbackFormProps {
  user: User;
  onClose: () => void;
  initialType?: FeedbackType;
  initialView?: string;
  initialPlan?: string;
  initialLanguage?: string;
}

export const FeedbackForm: React.FC<FeedbackFormProps> = ({
  user,
  onClose,
  initialType,
  initialView,
  initialPlan,
  initialLanguage,
}) => {
  const [type, setType] = useState<FeedbackType>(initialType || 'general');
  const [rating, setRating] = useState<number>(0);
  const [message, setMessage] = useState('');
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [xpAwarded, setXpAwarded] = useState(false);

  const MAX_MESSAGE_LENGTH = 500;

  const feedbackTypes: { value: FeedbackType; label: string; icon: React.ReactNode; description: string }[] = [
    { value: 'bug', label: 'Bug Report', icon: <Bug size={20} />, description: 'Something is broken' },
    { value: 'suggestion', label: 'Suggestion', icon: <Lightbulb size={20} />, description: 'Ideas for improvement' },
    { value: 'general', label: 'General Feedback', icon: <MessageSquare size={20} />, description: 'Share your thoughts' },
  ];

  const handleScreenshotChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setError('Screenshot size must be less than 5MB');
        return;
      }
      setScreenshot(file);
      const reader = new FileReader();
      reader.onload = (ev) => setScreenshotPreview(ev.target?.result as string);
      reader.readAsDataURL(file);
      setError(null);
    }
  };

  const removeScreenshot = () => {
    setScreenshot(null);
    setScreenshotPreview(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (rating === 0) {
      setError('Please provide a rating');
      return;
    }

    if (!message.trim()) {
      setError('Please enter your feedback message');
      return;
    }

    if (message.length > MAX_MESSAGE_LENGTH) {
      setError(`Message too long. Maximum ${MAX_MESSAGE_LENGTH} characters.`);
      return;
    }

    setLoading(true);

    try {
      const result = await submitFeedback(
        user.id,
        user.name,
        user.email,
        type,
        rating,
        message,
        screenshot || undefined,
        {
          view: initialView,
          plan: initialPlan || user.plan,
          language: initialLanguage,
        }
      );

      setXpAwarded(result.xpAwarded);
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 3000); // Increased timeout to show XP message
    } catch (err: any) {
      setError(err.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
        <div className="bg-slate-900 w-full max-w-lg rounded-[2.5rem] border border-emerald-500/50 p-8 space-y-6 animate-scaleIn text-center">
          <div className="flex justify-center">
            <div className="p-4 bg-emerald-500/10 rounded-full">
              <CheckCircle2 size={48} className="text-emerald-500" />
            </div>
          </div>
          <h2 className="text-2xl font-black text-white">Thank You!</h2>
          <p className="text-slate-400">Your feedback has been submitted successfully.</p>
          {xpAwarded && (
            <div className="mt-4 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
              <p className="text-emerald-400 font-black text-lg">+500 XP</p>
              <p className="text-slate-500 text-sm mt-1">Experience points added to your account!</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-slate-900 w-full max-w-2xl rounded-[2.5rem] border border-slate-800 p-8 space-y-6 animate-scaleIn max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-black text-white flex items-center gap-3">
              <MessageSquare className="text-emerald-400" size={28} />
              Help Us Improve
            </h2>
            <p className="text-slate-500 text-sm mt-1">Your feedback helps us build a better VitaCore</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 bg-slate-800 rounded-xl text-white hover:bg-slate-700 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/50 text-red-400 p-4 rounded-xl flex items-center gap-3">
            <AlertCircle size={20} />
            <span className="text-sm font-medium">{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Feedback Type Selection */}
          <div>
            <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-3">
              Feedback Type
            </label>
            <div className="grid grid-cols-3 gap-3">
              {feedbackTypes.map((ft) => (
                <button
                  key={ft.value}
                  type="button"
                  onClick={() => {
                    setType(ft.value);
                    setError(null);
                  }}
                  className={`p-4 rounded-2xl border-2 transition-all ${
                    type === ft.value
                      ? 'border-emerald-500 bg-emerald-500/10'
                      : 'border-slate-800 bg-slate-800/30 hover:border-slate-700'
                  }`}
                >
                  <div className={`mb-2 ${type === ft.value ? 'text-emerald-400' : 'text-slate-500'}`}>
                    {ft.icon}
                  </div>
                  <div className="text-xs font-black text-white uppercase tracking-tight">{ft.label}</div>
                  <div className="text-[10px] text-slate-500 mt-1">{ft.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Rating */}
          <div>
            <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-3">
              Rating <span className="text-red-400">*</span>
            </label>
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => {
                    setRating(star);
                    setError(null);
                  }}
                  className="transition-transform hover:scale-110 active:scale-90"
                >
                  <Star
                    size={32}
                    className={star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-slate-700'}
                  />
                </button>
              ))}
              {rating > 0 && (
                <span className="ml-4 text-sm font-bold text-slate-400">
                  {rating === 1 && 'Poor'}
                  {rating === 2 && 'Fair'}
                  {rating === 3 && 'Good'}
                  {rating === 4 && 'Very Good'}
                  {rating === 5 && 'Excellent'}
                </span>
              )}
            </div>
          </div>

          {/* Message */}
          <div>
            <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-3">
              Message <span className="text-red-400">*</span>
            </label>
            <textarea
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                setError(null);
              }}
              placeholder="Describe your feedback in detail..."
              className="w-full h-32 bg-slate-800 border border-slate-700 rounded-xl p-4 outline-none focus:border-emerald-500 text-white resize-none text-sm"
              maxLength={MAX_MESSAGE_LENGTH}
            />
            <div className="flex justify-between items-center mt-2">
              <p className="text-[10px] text-slate-500">
                {type === 'bug' && 'Include steps to reproduce if possible'}
                {type === 'suggestion' && 'Describe what you would like to see'}
                {type === 'general' && 'Share your thoughts and experience'}
              </p>
              <span className={`text-[10px] font-bold ${message.length > MAX_MESSAGE_LENGTH * 0.9 ? 'text-orange-400' : 'text-slate-500'}`}>
                {message.length} / {MAX_MESSAGE_LENGTH}
              </span>
            </div>
          </div>

          {/* Screenshot */}
          <div>
            <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-3">
              Screenshot (Optional)
            </label>
            {screenshotPreview ? (
              <div className="relative">
                <img src={screenshotPreview} alt="Screenshot preview" className="w-full rounded-xl border border-slate-700 max-h-48 object-contain bg-slate-800" />
                <button
                  type="button"
                  onClick={removeScreenshot}
                  className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <label className="block w-full p-6 border-2 border-dashed border-slate-700 rounded-xl hover:border-emerald-500/50 transition-colors cursor-pointer bg-slate-800/30">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleScreenshotChange}
                  className="hidden"
                />
                <div className="flex flex-col items-center gap-3">
                  <ImageIcon size={32} className="text-slate-500" />
                  <span className="text-sm text-slate-400 font-medium">Click to upload screenshot</span>
                  <span className="text-[10px] text-slate-600">Max 5MB (PNG, JPG, GIF)</span>
                </div>
              </label>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-4 bg-slate-800 text-white rounded-xl font-black uppercase tracking-widest text-xs hover:bg-slate-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || rating === 0 || !message.trim()}
              className="flex-1 px-6 py-4 bg-emerald-500 text-white rounded-xl font-black uppercase tracking-widest text-xs hover:bg-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send size={16} />
                  Submit Feedback
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

